import { computed, inject } from '@angular/core';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { WebSocketService } from '@qtek/core/websockets-core';
import {
  DashboardTile,
  DashboardTileDataset,
  DashboardTiles,
  MenuNode,
  MenuSetupNode,
} from '@qtek/shared/models';
import { getScopesFromMenuConfigByUrl } from '@qtek/shared/utils';
import {
  Observable,
  Subject,
  catchError,
  filter,
  mergeMap,
  of,
  pipe,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';
import { MenuService } from './menu.service';

interface MenuState {
  mainMenu: MenuNode[];
  setupMenu: MenuSetupNode[];
  dashboardMenu: DashboardTiles;
  websocketDatasets: Record<string, DashboardTileDataset[]>;
}

const initialState: MenuState = {
  mainMenu: [],
  setupMenu: [],
  dashboardMenu: null,
  websocketDatasets: {},
};

export const MenuStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),

  withMethods(
    (
      store,
      menuService = inject(MenuService),
      webSocketService = inject(WebSocketService)
    ) => {
      const disconnect$ = new Subject<string>();

      return {
        loadMain: rxMethod<void>(
          pipe(
            switchMap(() =>
              menuService.getMainMenu().pipe(
                tap(({ res }) => patchState(store, { mainMenu: res.children })),
                catchError(() => of(null))
              )
            )
          )
        ),

        loadSetup: rxMethod<void>(
          pipe(
            switchMap(() =>
              menuService.getSetupMenu().pipe(
                tap(({ res }) =>
                  patchState(store, { setupMenu: res.children })
                ),
                catchError(() => of(null))
              )
            )
          )
        ),

        loadDashboard(): Observable<DashboardTiles> {
          return menuService.getDashboardMenu().pipe(
            tap(tiles => patchState(store, { dashboardMenu: tiles })),
            catchError(() => of(null))
          );
        },

        connectDashboard: rxMethod<string>(
          pipe(
            tap(mysid =>
              menuService.subscribeDashboard({ id: mysid, mysid, s: 'report' })
            ),
            mergeMap(mysid =>
              webSocketService.getWebSocket$().pipe(
                filter(data => data.mysid === mysid),
                takeUntil(disconnect$.pipe(filter(id => id === mysid))),
                tap((data: any) => {
                  if (data.op === 'upds' && data.res) {
                    const tiles = store.dashboardMenu();
                    const labelledData: DashboardTileDataset[] = data.res.map(
                      (dataset: DashboardTileDataset) => {
                        const originalDataset = tiles?.tiles
                          ?.find(
                            (tile: DashboardTile) =>
                              tile.datasource?.tp === mysid
                          )
                          ?.datasets?.find(
                            (ds: DashboardTileDataset) => ds.id === dataset.id
                          );
                        return originalDataset
                          ? { ...originalDataset, ...dataset }
                          : dataset;
                      }
                    );
                    patchState(store, state => ({
                      websocketDatasets: {
                        ...state.websocketDatasets,
                        [mysid]: labelledData,
                      },
                    }));
                  }
                }),
                catchError(() => of(null))
              )
            )
          )
        ),

        disconnectDashboard(mysid: string): void {
          disconnect$.next(mysid);
          menuService.unsubscribeDashboard({ mysid });
        },

        pageScopesFromMenu(url: string) {
          return computed(() =>
            getScopesFromMenuConfigByUrl(store.mainMenu(), url)
          );
        },

        websocketDatasetById(id: string) {
          return computed(() => store.websocketDatasets()[id]);
        },
      };
    }
  )
);
